/**
 * METRIC CARD
 *
 * Individual stat card displaying a key metric with large number value.
 * Used in report dashboards to highlight important numbers.
 *
 * Supports optional icon and trend indicator.
 */

import React from 'react'
import { token } from '@atlaskit/tokens'

/**
 * Format large numbers with K/M suffix
 *
 * @param {number} value - Number to format
 * @returns {string} Formatted number string
 */
const formatNumber = (value) => {
  if (value === null || value === undefined) {
    return '-'
  }

  if (typeof value === 'string') {
    return value
  }

  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toLocaleString()
}

/**
 * MetricCard Component
 *
 * @param {object} props
 * @param {number|string} props.value - The metric value to display
 * @param {string} props.label - Label text below the value
 * @param {React.ReactNode} props.icon - Optional icon to display
 * @param {string} props.trend - Optional trend indicator: 'up', 'down', or null
 * @param {string} props.trendValue - Optional trend value (e.g., "+5%")
 * @param {string} props.color - Optional custom color for the value
 * @param {boolean} props.compact - Whether to use compact styling (default: false)
 */
const MetricCard = ({
  value,
  label,
  icon,
  trend,
  trendValue,
  color,
  compact = false
}) => {
  // Get trend color
  const getTrendColor = () => {
    if (trend === 'up') {
      return token('color.text.success', '#22A06B')
    }
    if (trend === 'down') {
      return token('color.text.danger', '#DE350B')
    }
    return token('color.text.subtle', '#6B778C')
  }

  // Get trend arrow
  const getTrendArrow = () => {
    if (trend === 'up') return '↑'
    if (trend === 'down') return '↓'
    return ''
  }

  return (
    <div
      style={{
        backgroundColor: token('elevation.surface.raised', '#FFFFFF'),
        borderRadius: '8px',
        padding: compact ? '12px 16px' : '16px 20px',
        boxShadow: token('elevation.shadow.raised', '0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)'),
        display: 'flex',
        flexDirection: 'column',
        minWidth: compact ? '100px' : '120px'
      }}
    >
      {/* Icon row (if provided) */}
      {icon && (
        <div
          style={{
            marginBottom: '8px',
            color: token('color.icon', '#6B778C')
          }}
        >
          {icon}
        </div>
      )}

      {/* Value */}
      <div
        style={{
          fontSize: compact ? '24px' : '32px',
          fontWeight: 600,
          color: color || token('color.text', '#172B4D'),
          lineHeight: 1.2,
          letterSpacing: '-0.02em'
        }}
      >
        {formatNumber(value)}
      </div>

      {/* Label */}
      <div
        style={{
          fontSize: compact ? '11px' : '12px',
          color: token('color.text.subtle', '#6B778C'),
          marginTop: compact ? '4px' : '6px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontWeight: 500
        }}
      >
        {label}
      </div>

      {/* Trend indicator (if provided) */}
      {(trend || trendValue) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginTop: '8px',
            fontSize: '12px',
            color: getTrendColor()
          }}
        >
          {getTrendArrow()}
          {trendValue && <span>{trendValue}</span>}
        </div>
      )}
    </div>
  )
}

export default MetricCard
